import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";

interface FeedbackFormData {
  rating: number;
  message: string;
}

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FeedbackFormData) => void;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FeedbackFormData>({
    defaultValues: {
      rating: 0,
      message: "",
    },
    mode: "onChange",
  });

  const rating = watch("rating");

  // Handle star click
  const handleRatingChange = (newRating: number) => {
    setValue("rating", newRating, { shouldValidate: true });
  };

  // Handle form submission
  const onFormSubmit = (data: FeedbackFormData) => {
    onSubmit(data);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Feedback</DialogTitle>
      <DialogContent>
        <DialogContentText>
          We would love to hear your feedback. Please rate your experience and
          leave a comment below.
        </DialogContentText>

        <Box display="flex" justifyContent="center" mt={2} mb={2}>
          {[1, 2, 3, 4, 5].map((star) => (
            <IconButton
              key={star}
              onClick={() => handleRatingChange(star)}
              color="primary"
            >
              {star <= rating ? <Star /> : <StarBorder />}
            </IconButton>
          ))}
        </Box>

        {errors.rating && (
          <p style={{ color: "red", textAlign: "center" }}>
            Rating is required
          </p>
        )}

        <Controller
          name="message"
          control={control}
          rules={{ required: "Please enter your feedback message" }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Your Message"
              multiline
              rows={4}
              placeholder="Write your feedback here..."
              variant="outlined"
              error={!!errors.message}
              helperText={errors.message ? errors.message.message : ""}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onFormSubmit)}
          color="primary"
          disabled={!isValid}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
